import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ProjectAssignmentItem } from '../../types/assignmentPointsNote';
import { buildProjectMemberReachRows } from '../../utils/assignmentPointsNoteProgress';
import type { FlatMember } from './GeneralAssignmentSettingsModal';

type Props = {
  open: boolean;
  item: ProjectAssignmentItem;
  members: FlatMember[];
  onClose: () => void;
};

const ProjectMemberReachModal: React.FC<Props> = ({ open, item, members, onClose }) => {
  const [sortOrder, setSortOrder] = useState<'number' | 'rate'>('number');

  const rows = useMemo(() => {
    const data = buildProjectMemberReachRows(item, members).filter((r) =>
      item.assignedMemberIds.includes(r.memberId)
    );
    if (sortOrder === 'rate') {
      return data.slice().sort((a, b) => b.completionRate - a.completionRate || a.orderIndex - b.orderIndex);
    }
    return data.slice().sort((a, b) => a.orderIndex - b.orderIndex);
  }, [item, members, sortOrder]);

  if (!open) return null;

  return createPortal(
    <div className="cp-apn-reach-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-apn-reach-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-apn-reach-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="cp-apn-reach-title" className="cp-apn-reach-title">
          멤버별 도달률 확인하기
        </h2>
        <div className="cp-apn-reach-sort">
          <button
            type="button"
            className={`cp-apn-reach-sort-btn${sortOrder === 'number' ? ' is-active' : ''}`}
            onClick={() => setSortOrder('number')}
          >
            번호순
          </button>
          <button
            type="button"
            className={`cp-apn-reach-sort-btn${sortOrder === 'rate' ? ' is-active' : ''}`}
            onClick={() => setSortOrder('rate')}
          >
            도달률순
          </button>
        </div>
        <div className="cp-apn-reach-list">
          {rows.length === 0 ? (
            <p className="cp-apn-empty-hint--center">배정된 멤버가 없습니다.</p>
          ) : (
            rows.map((row) => (
              <div key={row.memberId} className="cp-apn-reach-card">
                <div className="cp-apn-reach-card-head">
                  <span>
                    {row.orderIndex > 0 ? `${row.orderIndex}. ` : ''}
                    {row.memberName}
                  </span>
                  <span className="cp-apn-reach-pct">{row.completionRate}%</span>
                </div>
                <div className="cp-apn-reach-bar" aria-hidden>
                  {row.segmentStates.map((done, i) => (
                    <span
                      key={`${row.memberId}-${i}`}
                      className={`cp-apn-reach-seg${done ? ' is-done' : ''}`}
                    />
                  ))}
                  {row.totalSubTasks === 0 ? (
                    <span className="cp-apn-reach-seg" style={{ flex: 1 }} />
                  ) : null}
                </div>
                <div className="cp-apn-reach-meta">
                  확인 완료 {row.completedCount} / 전체 {row.totalSubTasks}개
                </div>
              </div>
            ))
          )}
        </div>
        <div className="cp-apn-reach-footer">
          <button type="button" className="cp-apn-create-btn-cancel" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProjectMemberReachModal;
